import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";

export default function WhatsAppButton({ phoneNumber = "524423412287" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");

  const defaultMessage = "Hola, tengo una pregunta sobre GATOFELIZ...";

  const handleSendMessage = () => {
    const text = message.trim() || defaultMessage;
    const encodedMessage = encodeURIComponent(text);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, "_blank");
    setIsOpen(false);
    setMessage("");
  };

  const handleQuickChat = () => {
    const encodedMessage = encodeURIComponent(defaultMessage);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, "_blank");
  };

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-4 md:right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#128C7E] rounded-full shadow-lg flex items-center justify-center transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        data-testid="whatsapp-float-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </motion.button>

      {/* Chat Popup */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="fixed bottom-24 right-4 md:right-6 z-50 w-72 md:w-80 bg-white rounded-2xl shadow-2xl overflow-hidden"
            data-testid="whatsapp-popup"
          >
            {/* Header */}
            <div className="bg-[#075E54] p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_mex-catnip-promo/artifacts/eeist4qj_LOGO%20FONDO%20TRANSPARENTE.png" 
                    alt="GatoFeliz" 
                    className="w-8 h-8 object-contain brightness-0 invert"
                  />
                </div>
                <div>
                  <h3 className="font-fredoka font-bold text-white">GATOFELIZ</h3>
                  <p className="text-xs text-white/70">Normalmente responde en minutos</p>
                </div>
              </div>
            </div>

            {/* Chat Body */}
            <div className="bg-[#ECE5DD] p-4 min-h-[120px]">
              <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%]">
                <p className="text-sm text-gray-700">
                  ¡Hola! 👋 ¿En qué podemos ayudarte? Escríbenos y te responderemos lo más pronto posible.
                </p>
                <span className="text-[10px] text-gray-400 float-right mt-1">
                  {new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            </div>

            {/* Input Area */}
            <div className="p-3 bg-[#F0F0F0] flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-4 py-2 rounded-full border-none text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]"
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                data-testid="whatsapp-input"
              />
              <button
                onClick={handleSendMessage}
                className="w-10 h-10 bg-[#25D366] hover:bg-[#128C7E] rounded-full flex items-center justify-center transition-colors"
                data-testid="whatsapp-send-btn"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tooltip on hover (when closed) */}
      {!isOpen && (
        <motion.div
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          className="fixed bottom-8 right-24 z-40 bg-white px-3 py-2 rounded-lg shadow-lg hidden md:block"
        >
          <p className="text-sm font-nunito text-gray-700">¿Necesitas ayuda?</p>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
            <div className="border-8 border-transparent border-l-white"></div>
          </div>
        </motion.div>
      )}
    </>
  );
}
